import { useI18n } from '../../i18n'
import AssetInventoryExecutiveRow from './AssetInventoryExecutiveRow'
import AssetInventoryExecutiveContextPanel from './AssetInventoryExecutiveContextPanel'
import AssetInventoryAttackSurfaceMiniGraph from './AssetInventoryAttackSurfaceMiniGraph'
import { useAssetInventoryExecutive } from './assetInventoryExecutiveContext'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import styles from '../../styles/components/assets/AssetsInventoryIntelligence.module.scss'

export function AssetInventoryPostureSection() {
  const { t } = useI18n()
  const { overviewStats, loading, posture, summary } = useAssetInventoryExecutive()

  return (
    <section className={entStyles.layer}>
      <p className={entStyles.layerLabel}>
        {t('enterpriseOperationalUx.assetInventory.layerPosture', 'Operational attack surface posture')}
      </p>
      <div className={entStyles.posturePanel}>
        <h2 className={entStyles.postureTitle}>
          {t('enterpriseOperationalUx.assetInventory.operationalAttackSurface', 'Operational Attack Surface')}
        </h2>
        <p className={entStyles.postureSubtitle}>
          {t(
            'enterpriseOperationalUx.assetInventory.workspaceHint',
            'Inventory scope, vulnerability exposure, and operational signals for the assets in view.',
          )}
        </p>

        {overviewStats ? (
          <div className={entStyles.postureGrid}>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>{t('assets.overview.totalAssets', 'Total Assets')}</p>
              <p className={entStyles.postureMetricValue}>{overviewStats.totalAssets}</p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>{t('assets.inventory.visibleAssets', 'Visible')}</p>
              <p className={entStyles.postureMetricValue}>{overviewStats.visibleAssets}</p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>
                {t('enterpriseOperationalUx.assetInventory.totalVulnerabilities', 'Total vulnerabilities')}
              </p>
              <p className={entStyles.postureMetricValue}>{overviewStats.totalVulnerabilities}</p>
              <p className={entStyles.postureSubtitle}>
                {t('enterpriseOperationalUx.assetInventory.vulnAcrossAssets', '{{count}} assets with findings', {
                  count: overviewStats.assetsWithVulnerabilities,
                })}
              </p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>
                {t('enterpriseOperationalUx.assetInventory.assetsWithVulns', 'Assets with vulnerabilities')}
              </p>
              <p className={entStyles.postureMetricValue}>{overviewStats.assetsWithVulnerabilities}</p>
              <p className={entStyles.postureSubtitle}>
                {t('enterpriseOperationalUx.assetInventory.vulnAssetRatio', '{{vulns}} vulnerabilities · {{assets}} assets', {
                  vulns: overviewStats.totalVulnerabilities,
                  assets: overviewStats.assetsWithVulnerabilities,
                })}
              </p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>{t('assets.inventory.filters.critical', 'Critical')}</p>
              <p className={entStyles.postureMetricValue}>{overviewStats.criticalVulnerabilities}</p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>{t('assets.inventory.filters.high', 'High')}</p>
              <p className={entStyles.postureMetricValue}>{overviewStats.highVulnerabilities}</p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>{t('severity.medium', 'Medium')}</p>
              <p className={entStyles.postureMetricValue}>{overviewStats.mediumVulnerabilities}</p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>{t('severity.low', 'Low')}</p>
              <p className={entStyles.postureMetricValue}>{overviewStats.lowVulnerabilities}</p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>{t('assets.overview.avgRiskScore', 'Avg risk score')}</p>
              <p className={entStyles.postureMetricValue}>{overviewStats.avgRiskScore.toFixed(1)}%</p>
            </div>
          </div>
        ) : null}

        {overviewStats && posture ? (
          <p className={entStyles.inventorySectionDivider}>
            {t('enterpriseOperationalUx.assetInventory.operationalSignals', 'Operational signals')}
          </p>
        ) : null}

        {loading ? <p className={entStyles.emptyState}>{t('common.loading', 'Loading…')}</p> : null}
        {posture ? (
          <div className={entStyles.postureGrid}>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>
                {t('enterpriseOperationalUx.assetInventory.criticalExposure', 'Critical exposure')}
              </p>
              <p className={entStyles.postureMetricValue}>{posture.criticalExposureAssets}</p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>
                {t('enterpriseOperationalUx.assetInventory.internetFacing', 'Internet-facing')}
              </p>
              <p className={entStyles.postureMetricValue}>{posture.internetFacingAssets}</p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>{t('enterpriseOperationalUx.degradedShort', 'degraded')}</p>
              <p className={entStyles.postureMetricValue}>{posture.degradedAssets}</p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>{t('enterpriseOperationalUx.staleEvidence', 'Stale evidence')}</p>
              <p className={entStyles.postureMetricValue}>{posture.staleEvidenceAssets}</p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>
                {t('enterpriseOperationalUx.dataMap.highestPressure', 'Highest pressure')}
              </p>
              <p className={entStyles.postureMetricValue}>{posture.highPressureAssets}</p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>
                {t('enterpriseOperationalUx.assetInventory.runtimeDegradation', 'Runtime degradation')}
              </p>
              <p className={entStyles.postureMetricValue}>{posture.runtimeInstabilityCount}</p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>
                {t('enterpriseOperationalUx.riskCompliance.blastRadiusConcentration', 'Blast radius')}
              </p>
              <p className={entStyles.postureMetricValue}>{posture.blastRadiusConcentration}</p>
            </div>
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>
                {t('enterpriseOperationalUx.assetInventory.assuranceCoverage', 'Continuous assurance')}
              </p>
              <p className={entStyles.postureMetricValue}>{posture.continuousAssuranceCoverage}</p>
            </div>
          </div>
        ) : null}
        {summary?.explanations?.length ? (
          <p className={entStyles.postureStory}>{summary.explanations.join(' ')}</p>
        ) : null}
      </div>
    </section>
  )
}

export function AssetInventoryGraphSignalsSection() {
  const { t } = useI18n()
  const {
    summary,
    graphOpen,
    setGraphOpen,
    mergedExecutiveAssets,
    setSelection,
  } = useAssetInventoryExecutive()

  return (
    <div className={styles.inventoryWorkspace} data-testid="asset-inventory-executive-graph">
      <section className={entStyles.layer} data-testid="asset-inventory-topology-graph">
        <p className={entStyles.layerLabel}>
          {t('enterpriseOperationalUx.assetInventory.topologyLayer', 'Asset topology')}
        </p>
        <div className={entStyles.posturePanel}>
          <div className={styles.aiGraphHeader}>
            <h2 className={entStyles.postureTitle}>
              {t('enterpriseOperationalUx.assetInventory.attackSurfaceGraph', 'Attack surface graph')}
            </h2>
            <button type="button" className={entStyles.outlineRefreshBtn} onClick={() => setGraphOpen((v) => !v)}>
              {graphOpen ? t('common.hide', 'Hide') : t('common.show', 'Show')}
            </button>
          </div>
          {graphOpen ? <AssetInventoryAttackSurfaceMiniGraph assets={mergedExecutiveAssets} /> : null}
        </div>
      </section>

      <section className={styles.aiSignalsRow}>
        <div className={styles.aiSignalBlock}>
          <h3 className={styles.aiSignalTitle}>{t('enterpriseOperationalUx.layerTimeline', 'Runtime operational timeline')}</h3>
          {summary?.runtimeEvents?.length ? (
            <ul className={styles.aiSignalList}>
              {summary.runtimeEvents.slice(0, 8).map((ev) => (
                <li key={ev.id}>
                  <button type="button" className={styles.aiSignalBtn} onClick={() => setSelection({ kind: 'runtime', event: ev })}>
                    <span className={styles.aiRuntimeDot} aria-hidden />
                    {ev.eventType} — {ev.summary}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.aiWidgetEmpty}>
              {t('enterpriseOperationalUx.assetInventory.noRuntimeInstability', 'No runtime instability')}
            </p>
          )}
        </div>
        <div className={styles.aiSignalBlock}>
          <h3 className={styles.aiSignalTitle}>{t('enterpriseOperationalUx.layerBlast', 'Shared control blast radius')}</h3>
          {summary?.blastRadiusImpacts?.length ? (
            <ul className={styles.aiSignalList}>
              {summary.blastRadiusImpacts.slice(0, 6).map((imp) => (
                <li key={imp.controlCode}>
                  <button type="button" className={styles.aiSignalBtn} onClick={() => setSelection({ kind: 'blast', impact: imp })}>
                    {imp.controlCode} — {imp.summary}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.aiWidgetEmpty}>
              {t('enterpriseOperationalUx.assetInventory.noBlastPropagation', 'No blast radius propagation')}
            </p>
          )}
        </div>
      </section>
    </div>
  )
}

export function AssetInventoryListSection() {
  const { t } = useI18n()
  const {
    assets,
    loading,
    filteredExecutive,
    selectedId,
    setSelectedId,
    selection,
    setSelection,
    selectedAsset,
    loadSummary,
    onEdit,
    onDelete,
    onScan,
    onOpenSystem,
  } = useAssetInventoryExecutive()

  const openByDefault = filteredExecutive.length > 0 && filteredExecutive.length < assets.length

  return (
    <div className={entStyles.inventoryFiltersExtension} data-testid="asset-inventory-list">
      <details className={entStyles.legacyDetails} open={openByDefault || undefined}>
        <summary className={entStyles.legacyDetailsSummary}>
          {t('enterpriseOperationalUx.assetInventory.assetListExpand', 'Asset inventory list')}
          {filteredExecutive.length > 0 ? ` (${filteredExecutive.length})` : ''}
        </summary>
        <div className={styles.aiExecutiveLayout}>
          <div className={styles.aiExecutiveMain}>
            <div className={styles.aiExecutiveToolbar}>
              <button type="button" className={entStyles.outlineRefreshBtn} onClick={() => void loadSummary()}>
                {t('common.refresh', 'Refresh')}
              </button>
            </div>

            {loading ? (
              <p className={styles.aiWidgetEmpty}>{t('common.loading')}</p>
            ) : filteredExecutive.length === 0 ? (
              <p className={styles.aiWidgetEmpty}>
                {assets.length === 0
                  ? t('assets.inventory.empty', 'No assets in this organization yet.')
                  : t('assets.inventory.noMatches', 'No assets match the current filters.')}
              </p>
            ) : (
              <div className={styles.aiExecutiveList}>
                {filteredExecutive.map((asset) => (
                  <AssetInventoryExecutiveRow
                    key={String(asset.assetId)}
                    asset={asset}
                    selected={selectedId === String(asset.assetId)}
                    onSelect={() => {
                      setSelectedId(String(asset.assetId))
                      setSelection({ kind: 'asset', asset })
                    }}
                    onOpenGraph={() => {
                      setSelectedId(String(asset.assetId))
                      setSelection({ kind: 'exposure', asset })
                    }}
                    onEdit={onEdit ? () => onEdit(String(asset.assetId)) : undefined}
                    onDelete={onDelete ? () => onDelete(String(asset.assetId)) : undefined}
                    onOpenSystem={onOpenSystem}
                  />
                ))}
              </div>
            )}
          </div>

          <aside className={styles.aiExecutiveAside}>
            <AssetInventoryExecutiveContextPanel selection={selection} onClose={() => setSelection(null)} />
            {selectedAsset && onEdit ? (
              <div className={styles.aiLegacyActions}>
                <button type="button" className={styles.aiActionBtn} onClick={() => onEdit(String(selectedAsset.assetId))}>
                  {t('common.edit', 'Edit')}
                </button>
                {onScan ? (
                  <button
                    type="button"
                    className={styles.aiActionBtn}
                    onClick={() => {
                      const ref = assets.find((a) => String(a.id || a.assetId) === String(selectedAsset.assetId))
                      if (ref) onScan(ref)
                    }}
                  >
                    {t('assets.scan', 'Scan')}
                  </button>
                ) : null}
              </div>
            ) : null}
          </aside>
        </div>
      </details>
    </div>
  )
}
